import { groqLLM } from "./groq";
import { anthropicLLM } from "./anthropic";
import { geminiLLM } from "./gemini";
import { openaiLLM } from "./openai";
import { Component, LLMProvider, T_RESPONSE } from "../types";
import dotenv from 'dotenv';
import { logger } from "../utils/logger";

dotenv.config({ quiet: true });

// Export types and singletons for model strategy configuration
// Note: ModelStrategy is exported from types.ts (defined there for SDK config)
export { TaskType, BaseLLMConfig } from './base-llm';
export { anthropicLLM } from './anthropic';
export { groqLLM } from './groq';
export { geminiLLM } from './gemini';
export { openaiLLM } from './openai';



/**
 * Parse LLM_PROVIDERS from environment variable
 * Expects a stringified JSON array like: '["anthropic","groq"]'
 */
export function getLLMProviders(): LLMProvider[] {
    const envProviders = process.env.LLM_PROVIDERS;

    const DEFAULT_PROVIDERS: LLMProvider[] = ['anthropic', 'gemini', 'openai', 'groq'];
    if (!envProviders) {
        // Default to anthropic if not specified
        return DEFAULT_PROVIDERS;
    }

    try {
        const providers = JSON.parse(envProviders) as LLMProvider[];

        // Validate providers
        const validProviders = providers.filter(p => p === 'anthropic' || p === 'groq' || p === 'gemini' || p === 'openai');

        if (validProviders.length === 0) {
            return DEFAULT_PROVIDERS;
        }

        return validProviders;
    } catch (error) {
        logger.error('Failed to parse LLM_PROVIDERS, defaulting to ["anthropic"]:', error);
        return DEFAULT_PROVIDERS;
    }
}

/**
 * Method 1: Use Anthropic Claude LLM
 */
export const useAnthropicMethod = async (
    prompt: string,
    components: Component[],
    apiKey?: string,
    conversationHistory?: string,
    responseMode: 'component' | 'text' = 'component',
    streamCallback?: (chunk: string) => void,
    collections?: any,
    externalTools?: any[],
    userId?: string
): Promise<T_RESPONSE> => {

    // Only check components for component mode
    if (responseMode === 'component' && components.length === 0) {
        const emptyMsg = 'Components not loaded in memory. Please ensure components are fetched first.';
        logger.error('[useAnthropicMethod] No components available');
        return { success: false, errors: [emptyMsg] };
    }

    const matchResult = await anthropicLLM.handleUserRequest(prompt, components, apiKey, conversationHistory, responseMode, streamCallback, collections, externalTools, userId);
    logger.info(`[useAnthropicMethod] Successfully generated ${responseMode} using Anthropic`);
    return matchResult;
};

/**
 * Method 2: Use Groq LLM
 */
export const useGroqMethod = async (
    prompt: string,
    components: Component[],
    apiKey?: string,
    conversationHistory?: string,
    responseMode: 'component' | 'text' = 'component',
    streamCallback?: (chunk: string) => void,
    collections?: any,
    externalTools?: any[],
    userId?: string
): Promise<T_RESPONSE> => {
    logger.debug('[useGroqMethod] Initializing Groq LLM matching method');
    logger.debug(`[useGroqMethod] Response mode: ${responseMode}`);

    // Only check components for component mode
    if (responseMode === 'component' && components.length === 0) {
        const emptyMsg = 'Components not loaded in memory. Please ensure components are fetched first.';
        logger.error('[useGroqMethod] No components available');
        return { success: false, errors: [emptyMsg] };
    }

    logger.debug(`[useGroqMethod] Processing with ${components.length} components`);

    const matchResult = await groqLLM.handleUserRequest(prompt, components, apiKey, conversationHistory, responseMode, streamCallback, collections, externalTools, userId);
    logger.info(`[useGroqMethod] Successfully generated ${responseMode} using Groq`);
    return matchResult;
}

/**
 * Method 3: Use Google Gemini LLM
 */
export const useGeminiMethod = async (
    prompt: string,
    components: Component[],
    apiKey?: string,
    conversationHistory?: string,
    responseMode: 'component' | 'text' = 'component',
    streamCallback?: (chunk: string) => void,
    collections?: any,
    externalTools?: any[],
    userId?: string
): Promise<T_RESPONSE> => {
    logger.debug('[useGeminiMethod] Initializing Gemini LLM matching method');
    logger.debug(`[useGeminiMethod] Response mode: ${responseMode}`);

    // Only check components for component mode
    if (responseMode === 'component' && components.length === 0) {
        const emptyMsg = 'Components not loaded in memory. Please ensure components are fetched first.';
        logger.error('[useGeminiMethod] No components available');
        return { success: false, errors: [emptyMsg] };
    }

    logger.debug(`[useGeminiMethod] Processing with ${components.length} components`);

    const matchResult = await geminiLLM.handleUserRequest(prompt, components, apiKey, conversationHistory, responseMode, streamCallback, collections, externalTools, userId);
    logger.info(`[useGeminiMethod] Successfully generated ${responseMode} using Gemini`);
    return matchResult;
}

/**
 * Method 4: Use OpenAI GPT LLM
 */
export const useOpenAIMethod = async (
    prompt: string,
    components: Component[],
    apiKey?: string,
    conversationHistory?: string,
    responseMode: 'component' | 'text' = 'component',
    streamCallback?: (chunk: string) => void,
    collections?: any,
    externalTools?: any[],
    userId?: string
): Promise<T_RESPONSE> => {
    logger.debug('[useOpenAIMethod] Initializing OpenAI GPT matching method');
    logger.debug(`[useOpenAIMethod] Response mode: ${responseMode}`);

    // Only check components for component mode
    if (responseMode === 'component' && components.length === 0) {
        const emptyMsg = 'Components not loaded in memory. Please ensure components are fetched first.';
        logger.error('[useOpenAIMethod] No components available');
        return { success: false, errors: [emptyMsg] };
    }

    logger.debug(`[useOpenAIMethod] Processing with ${components.length} components`);

    const matchResult = await openaiLLM.handleUserRequest(prompt, components, apiKey, conversationHistory, responseMode, streamCallback, collections, externalTools, userId);
    logger.info(`[useOpenAIMethod] Successfully generated ${responseMode} using OpenAI`);
    return matchResult;
}


/**
 * Get user response with automatic fallback between LLM providers
 * Tries providers in order specified by LLM_PROVIDERS or passed parameter
 * @param responseMode - 'component' for component generation (default), 'text' for text responses
 * @param streamCallback - Optional callback function to receive text chunks as they stream (only for text mode)
 * @param collections - Collection registry for executing database queries (required for text mode with queries)
 * @param externalTools - Optional array of external tools (email, calendar, etc.) that can be called (only for text mode)
 */
export const get_user_response = async (
    prompt: string,
    components: Component[],
    anthropicApiKey?: string,
    groqApiKey?: string,
    geminiApiKey?: string,
    openaiApiKey?: string,
    llmProviders?: LLMProvider[],
    conversationHistory?: string,
    responseMode: 'component' | 'text' = 'component',
    streamCallback?: (chunk: string) => void,
    collections?: any,
    externalTools?: any[],
    userId?: string
): Promise<T_RESPONSE> => {

    const providers = llmProviders || getLLMProviders();
    const errors: string[] = [];

    logger.info(`[get_user_response] LLM Provider order: [${providers.join(', ')}]`);

    for (let i = 0; i < providers.length; i++) {
        const provider = providers[i];
        const isLastProvider = i === providers.length - 1;

        logger.info(`[get_user_response] Attempting provider: ${provider} (${i + 1}/${providers.length})`);

        let result: T_RESPONSE;
        if (provider === 'anthropic') {
            result = await useAnthropicMethod(prompt, components, anthropicApiKey, conversationHistory, responseMode, streamCallback, collections, externalTools, userId);
        } else if (provider === 'groq') {
            result = await useGroqMethod(prompt, components, groqApiKey, conversationHistory, responseMode, streamCallback, collections, externalTools, userId);
        } else if (provider === 'gemini') {
            result = await useGeminiMethod(prompt, components, geminiApiKey, conversationHistory, responseMode, streamCallback, collections, externalTools, userId);
        } else if (provider === 'openai') {
            result = await useOpenAIMethod(prompt, components, openaiApiKey, conversationHistory, responseMode, streamCallback, collections, externalTools, userId);
        } else {
            logger.warn(`[get_user_response] Unknown provider: ${provider} - skipping`);
            errors.push(`Unknown provider: ${provider}`);
            continue; // Skip unknown providers
        }

        if (result.success) {
            logger.info(`[get_user_response] Success with provider: ${provider}`);
            return result;
        } else {
            // Collect errors from result
            const providerErrors = result.errors.map(err => `${provider}: ${err}`);
            errors.push(...providerErrors);
            logger.warn(`[get_user_response] Provider ${provider} returned unsuccessful result: ${result.errors.join(', ')}`);

            // If this is not the last provider, try the next one
            if (!isLastProvider) {
                logger.info('[get_user_response] Falling back to next provider...');
            }
        }
    }

    // All providers failed
    logger.error(`[get_user_response] All LLM providers failed. Errors: ${errors.join('; ')}`);

    return {
        success: false,
        errors
    };
}

